'use client'
import type { FC } from 'react'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
import { useTranslator } from '@/hooks'
import { vw, mq } from '@/styles'
import { LinkStyled } from '@/components'
import type { TypeLang } from '@/utils'

type Props = {
  type?: 'homepage' |'thanks'
  data?: {
    title: string
    description: string
  }
}

const link = {
  'es':{
    'bookings' : 'https://widget.thefork.com/es/328be200-9519-4ad6-a6d9-abbf18cdeca3'
  },
  'cat':{
    'bookings' : 'https://widget.thefork.com/ca/328be200-9519-4ad6-a6d9-abbf18cdeca3'
  },
  'fr':{
    'bookings' : 'https://widget.thefork.com/fr/328be200-9519-4ad6-a6d9-abbf18cdeca3'
  },
  'en':{
    'bookings' : 'https://widget.thefork.com/en/328be200-9519-4ad6-a6d9-abbf18cdeca3'
  },
  'it': {
    'bookings' : 'https://widget.thefork.com/it/328be200-9519-4ad6-a6d9-abbf18cdeca3'
  }
}

export const Main:FC<Props> = ({ type = 'homepage', data }) => {
  const { t } = useTranslator()
  const { lang } = useParams() as { lang: TypeLang }

  return (
    <MainWrapper data-type={type}>
			{(type === 'thanks' && data) && <hgroup>
				<h2>{data.title}</h2>
				<p>{data.description}</p>
			</hgroup>}
      <LinkStyled href={`/pdf/iluzione_${lang}.pdf`} target='_blank' rel='noopener noreferrer' outline>{t('layout.main.menu')}</LinkStyled>
      {type === 'homepage' && <LinkStyled href={`${link[lang].bookings}`} target='_blank' rel='noopener noreferrer' outline>{t('layout.main.book')}</LinkStyled>}
    </MainWrapper>
  )
}

const MainWrapper = styled.main<any>`
  align-items: center;
  display: flex;
  flex-direction: column;
  grid-column: 1 / span 6;
  grid-row: 1 / span 3;
  justify-content: flex-end;
  position: relative;
  width: 100%;
  z-index: 91;

  a, button {
    cursor: pointer;
  }

  ${mq.lessThan('nexus7')} {
    margin-bottom: 87.246px;
  }

  ${mq.lessThan('tablet')} {
    margin-bottom:${vw(60, 'mobile')};
  }

  ${mq.greaterThan('tablet')} {
    flex-direction: row;
    justify-content: center;
    grid-column: 1 / span 12;
    margin-bottom: 0;
  }

  @media screen and (min-width: 320px) and (max-width: 767px) and (orientation: landscape) {
    flex-direction: row;
    justify-content: center;
    margin-bottom: 0;

    a {
      margin: 0 10px;
    }

    a:first-of-type {
      margin-bottom: 0 !important;
    }
  }

  a {

    @media screen and (min-width: 320px) and (max-width: 767px) and (orientation: landscape) {
      height: 87px;
      width: 260px;

      > div:last-of-type {
        font-size: 28px;
      }
    }

    height: ${vw(87, 'mobile')};
    width: ${vw(260, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      height: 126.5px;
      width: 378px;
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(87, 'desktop')};
      width: ${vw(260, 'desktop')};
    }

    ${mq.greaterThan('container')} {
      height: 70.63px;
      width: 211.06px;
    }
  }

  a:first-of-type {
    margin-bottom: ${vw(15, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      margin-bottom: 21.8px;
    }
  }

  ${mq.greaterThan('tablet')} {
    a:first-of-type {
      margin-bottom: unset;
      margin-right:20px;
      margin-left: auto;
    }

    a:last-of-type {
      margin-right: auto;
      margin-left:20px;
    }
  }

	&[data-type='thanks'] {
		flex-direction: column;
		justify-content: center;
		align-items: center;

		a {
			&:first-of-type {
				&:last-of-type {
					margin: 0 auto;
					margin-left: auto;
					margin-right: auto;
				}
			}
		}
	}

	hgroup {
		text-shadow: .5px .5px 2px rgba(0, 0, 0, .4);
		margin-bottom: 40px;

		h2, p {
			text-align: center;
		}

		h2 {
			font-size: 48px;
			margin-bottom: 10px;
		}

		p {
			font-size: 28px;
		}
	}
`
